import type { UserWidgetPreferenceBase } from '../types';

/**
 * Motor puro de empaquetado 2D para calcular el layout de los widgets.
 * Busca la primera coordenada (X, Y) disponible de izquierda a derecha, de arriba a abajo.
 */

// Interfaz interna para el grid bidimensional (matriz)
type GridMap = boolean[][];

export const MAX_COLS = 12;

/**
 * Inicializa un mapa del grid ocupado por los widgets actuales.
 */
const buildOccupiedMap = (layout: UserWidgetPreferenceBase[]): GridMap => {
  const map: GridMap = [];
  
  // Encontrar la altura máxima actual
  const maxH = layout.reduce((acc, item) => Math.max(acc, item.pos_y + item.row_span), 0);
  
  // Inicializar grid con falses
  for (let y = 0; y < Math.max(maxH + 10, 50); y++) {
    map[y] = new Array(MAX_COLS).fill(false);
  }

  // Marcar celdas ocupadas
  layout.forEach(item => {
    for (let y = item.pos_y; y < item.pos_y + item.row_span; y++) {
      for (let x = item.pos_x; x < item.pos_x + item.col_span; x++) {
        if (map[y] && typeof map[y][x] !== 'undefined') {
          map[y][x] = true;
        }
      }
    }
  });

  return map;
};

/**
 * Verifica si una posición específica está disponible para un tamaño dado.
 */
const isPositionFree = (map: GridMap, startX: number, startY: number, w: number, h: number): boolean => {
  if (startX + w > MAX_COLS) return false; // Se sale del grid horizontalmente

  for (let y = startY; y < startY + h; y++) {
    for (let x = startX; x < startX + w; x++) {
      // Si la fila no existe, se considera libre
      if (map[y] && map[y][x]) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Encuentra la primera posición (X, Y) libre para un widget.
 * Escanea de Izquierda a Derecha, de Arriba a Abajo.
 * 
 * @param currentLayout Array de preferencias actuales.
 * @param width Ancho (columnas) requerido.
 * @param height Alto (filas) requerido.
 * @returns Posición {x, y} ideal.
 */
export const findFirstAvailablePosition = (
  currentLayout: UserWidgetPreferenceBase[],
  width: number,
  height: number
): { x: number; y: number } => {
  const map = buildOccupiedMap(currentLayout);
  
  for (let y = 0; y < 1000; y++) { // Límite de seguridad
    for (let x = 0; x <= MAX_COLS - width; x++) {
      if (isPositionFree(map, x, y, width, height)) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: 0 }; // Fallback
};

/**
 * Agrupa los widgets y aplica un offset Y para evitar colisiones 
 * en una vista de múltiples grids en cascada.
 * 
 * @param layout Layout original.
 * @param namespaceOffset Modificador multiplicador (ej. categoría index * 100).
 */
export const applyCascadeYOffset = (
  layout: UserWidgetPreferenceBase[], 
  namespaceOffset: number
): UserWidgetPreferenceBase[] => {
  return layout.map(item => ({
    ...item,
    pos_y: item.pos_y + namespaceOffset
  }));
};
